/**
 * Merge freshly-fetched server state into a local form draft without throwing
 * away edits the server hasn't caught up with yet.
 *
 * Mutations invalidate their own cache tag, so a GET lands shortly after every
 * save. Applying that response wholesale overwrites the field the user is
 * currently typing in, which reads as the text deleting itself.
 *
 * A field stays pending until the server reports the same value the draft
 * holds. That is stricter than "until the PUT resolved": saving field A and
 * then immediately editing field B leaves A's refetch in flight, and that
 * response predates B's write, so it must not be allowed to blank B.
 *
 * @param {object} server Entity as the server currently reports it.
 * @param {object} local Local draft currently shown in the form.
 * @param {Array} pendingFields Names of fields edited locally and not yet
 * confirmed by the server.
 * @returns {object} `merged` state to display, and the `stillPending` subset.
 */
export function mergePendingEdits<T>(
  server: T,
  local: T,
  pendingFields: string[]
): { merged: T; stillPending: string[] } {
  const serverValues = server as unknown as Record<string, unknown>;
  const localValues = local as unknown as Record<string, unknown>;

  const stillPending = pendingFields.filter(
    (field) => serverValues[field] !== localValues[field]
  );

  const merged = { ...(server as unknown as object) } as Record<
    string,
    unknown
  >;
  stillPending.forEach((field) => {
    merged[field] = localValues[field];
  });

  return { merged: merged as unknown as T, stillPending };
}

export default mergePendingEdits;
